import { useEffect, useMemo, useRef, useState } from "react";
import { FiUpload, FiFileText, FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import * as xlsx from "xlsx";
import AdminSectionCard, {
  AdminButton,
  AdminField,
  AdminInput,
  AdminSelect,
  AdminTextarea,
  adminCn,
} from "./AdminSectionCard";

const fieldNameRegex = /^[A-Za-z_][A-Za-z0-9_]*$/;

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.gubcpc.com";
const ADMIN_EMAIL_ENDPOINT =
  process.env.REACT_APP_ADMIN_EMAIL_ENDPOINT || "/admin/email/send";

const AdminEmailSender = () => {
  const formRef = useRef(null);
  const accessToken = useSelector((state) => state.adminAuth?.accessToken);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [recipientsMode, setRecipientsMode] = useState("file");
  const [htmlMode, setHtmlMode] = useState("file");
  const [recipientsFileError, setRecipientsFileError] = useState(null);
  const [htmlFileError, setHtmlFileError] = useState(null);
  const [recipientsTextValue, setRecipientsTextValue] = useState("");
  const [htmlInput, setHtmlInput] = useState("");
  const [rawInput, setRawInput] = useState("");
  const [htmlPreview, setHtmlPreview] = useState("");
  const [rawPreview, setRawPreview] = useState("");
  const [recipientsFileInfo, setRecipientsFileInfo] = useState(null);
  const [htmlFileInfo, setHtmlFileInfo] = useState(null);
  const [previewSearch, setPreviewSearch] = useState("");
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileRows, setFileRows] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [isTableEdited, setIsTableEdited] = useState(false);
  const [confirmRemoveRecipients, setConfirmRemoveRecipients] = useState(false);
  const [confirmRemoveHtml, setConfirmRemoveHtml] = useState(false);

  // Modal states for professional dialogs
  const [colModal, setColModal] = useState({
    isOpen: false,
    mode: "add", // "add" or "rename"
    colIndex: null,
    value: "",
    error: ""
  });

  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    colIndex: null,
    colName: ""
  });

  const maxEmails = 1000;
  const maxChars = 100000;
  const maxFileSize = 2 * 1024 * 1024;
  const previewLimit = 1000;
  const inputBaseClass =
    "w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100";
  const selectClass =
    "w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-9 text-sm text-slate-800 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100";
  const buttonBaseClass =
    "cursor-pointer rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-100";

  const parseEmailList = (value) =>
    value
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  const normalizedRecipients = useMemo(() => {
    const unique = new Set(parseEmailList(recipientsTextValue));
    return Array.from(unique).filter(Boolean);
  }, [recipientsTextValue]);

  const hasEmailHeader = useMemo(() => {
    return fileHeaders.some((header) => header.toLowerCase() === "email");
  }, [fileHeaders]);

  const htmlPreviewDoc = useMemo(() => {
    if (!htmlPreview.trim()) {
      return "<p>No HTML yet.</p>";
    }

    if (/<html[\s>]/i.test(htmlPreview)) {
      return htmlPreview;
    }

    return `<!doctype html><html><head><meta charset="utf-8" /></head><body>${htmlPreview}</body></html>`;
  }, [htmlPreview]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHtmlPreview(htmlInput);
    }, 2000);

    return () => clearTimeout(timer);
  }, [htmlInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRawPreview(rawInput);
    }, 2000);

    return () => clearTimeout(timer);
  }, [rawInput]);

  const extractEmailsFromText = (content) => {
    const matches = content.match(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g) ?? [];
    return Array.from(new Set(matches.map((value) => value.trim()))).filter(
      Boolean
    );
  };

  const parseFileForPreview = async (file) => {
    const lowerName = file.name.toLowerCase();
    let rows = [];

    if (lowerName.endsWith(".csv")) {
      const text = await file.text();
      const workbook = xlsx.read(text, { type: "string" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return { headers: [], rows: [] };
      }
      const sheet = workbook.Sheets[sheetName];
      rows = xlsx.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
      });
    } else {
      const buffer = await file.arrayBuffer();
      const workbook = xlsx.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return { headers: [], rows: [] };
      }
      const sheet = workbook.Sheets[sheetName];
      rows = xlsx.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
      });
    }

    if (rows.length === 0) {
      return { headers: [], rows: [] };
    }

    const headerRow = rows[0] ?? [];
    const inferredHeaders = headerRow.map((cell) => String(cell ?? "").trim());
    const maxCols = Math.max(
      inferredHeaders.length,
      ...rows.map((row) => row.length)
    );
    const headers = inferredHeaders.map((header, index) =>
      header ? header : `Column${index + 1}`
    );
    while (headers.length < maxCols) {
      headers.push(`Column${headers.length + 1}`);
    }

    const dataRows = rows.slice(1).map((row) =>
      headers.map((_, index) => String(row[index] ?? "").trim())
    );

    return { headers, rows: dataRows };
  };

  const countEmailsInFile = async (file) => {
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) {
      const buffer = await file.arrayBuffer();
      const workbook = xlsx.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return 0;
      }
      const sheet = workbook.Sheets[sheetName];
      const csvText = xlsx.utils.sheet_to_csv(sheet);
      return extractEmailsFromText(csvText).length;
    }

    const text = await file.text();
    return extractEmailsFromText(text).length;
  };

  const handleRecipientsFileChange = async (event) => {
    setIsTableEdited(false);
    const file = event.target.files?.[0];
    if (!file) {
      setRecipientsFileError(null);
      setRecipientsFileInfo(null);
      setFileHeaders([]);
      setFileRows([]);
      return;
    }

    if (file.size > maxFileSize) {
      event.target.value = "";
      setRecipientsFileError("Recipients file must be 2MB or less.");
      setRecipientsFileInfo(null);
      setFileHeaders([]);
      setFileRows([]);
      return;
    }

    try {
      const count = await countEmailsInFile(file);
      if (count > maxEmails) {
        event.target.value = "";
        setRecipientsFileError(
          `File has ${count} emails. Limit is ${maxEmails}.`
        );
        setRecipientsFileInfo(null);
        setFileHeaders([]);
        setFileRows([]);
        return;
      }
      const previewData = await parseFileForPreview(file);
      setFileHeaders(previewData.headers);
      setFileRows(previewData.rows);
      setRecipientsFileError(null);
      setRecipientsFileInfo(
        `${file.name} (${(file.size / 1024).toFixed(1)} KB)`
      );
    } catch (err) {
      event.target.value = "";
      setRecipientsFileError("Could not read the file. Please try again.");
      setRecipientsFileInfo(null);
      setFileHeaders([]);
      setFileRows([]);
    }
  };

  const handleRemoveRecipientsFile = () => {
    const input = document.getElementById("recipientsFile");
    if (input) input.value = "";
    setRecipientsFileError(null);
    setRecipientsFileInfo(null);
    setFileHeaders([]);
    setFileRows([]);
    setIsTableEdited(false);
  };

  const handleRemoveHtmlFile = () => {
    const input = document.getElementById("htmlFile");
    if (input) input.value = "";
    setHtmlFileError(null);
    setHtmlFileInfo(null);
  };

  const handleHtmlFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setHtmlFileError(null);
      setHtmlFileInfo(null);
      return;
    }

    if (file.size > maxFileSize) {
      event.target.value = "";
      setHtmlFileError("HTML file must be 2MB or less.");
      setHtmlFileInfo(null);
      return;
    }

    setHtmlFileError(null);
    setHtmlFileInfo(`${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
  };

  const addCustomField = () => {
    if (isTableEdited) {
      handleSaveTable();
    } else {
      setIsTableEdited(false);
    }
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setCustomFields((fields) => [...fields, { id, name: "", values: "" }]);
  };

  const updateCustomField = (id, patch) => {
    if (isTableEdited) {
      handleSaveTable();
    } else {
      setIsTableEdited(false);
    }
    setCustomFields((fields) =>
      fields.map((field) =>
        field.id === id ? { ...field, ...patch } : field
      )
    );
  };

  const removeCustomField = (id) => {
    if (isTableEdited) {
      handleSaveTable();
    } else {
      setIsTableEdited(false);
    }
    setCustomFields((fields) => fields.filter((field) => field.id !== id));
  };

  const reservedNames = useMemo(() => {
    const base = recipientsMode === "file" ? fileHeaders : ["email"];
    return new Set(base.map((name) => name.toLowerCase()));
  }, [recipientsMode, fileHeaders]);

  const customFieldErrors = useMemo(() => {
    const errors = {};
    const seen = new Set();

    for (const field of customFields) {
      const name = field.name.trim();
      if (!name) {
        errors[field.id] = "Field name is required.";
        continue;
      }
      if (!fieldNameRegex.test(name)) {
        errors[field.id] = "Use letters, numbers, and underscores only.";
        continue;
      }
      const lower = name.toLowerCase();
      if (reservedNames.has(lower)) {
        errors[field.id] = "Field name already exists in the file.";
        continue;
      }
      if (seen.has(lower)) {
        errors[field.id] = "Field name is duplicated.";
        continue;
      }
      const values = field.values.trim();
      if (!values) {
        errors[field.id] = "Values are required.";
        continue;
      }
      if (values.includes(" ") && !values.includes(",")) {
        errors[field.id] = "Use commas to separate values.";
        continue;
      }
      seen.add(lower);
    }

    return errors;
  }, [customFields, reservedNames]);

  const customFieldValues = useMemo(() => {
    return customFields.map((field) =>
      field.values
        .split(",")
        .map((value) => value.trim())
    );
  }, [customFields]);

  const customFieldValueMap = useMemo(() => {
    const map = new Map();
    customFields.forEach((field, index) => {
      const key = field.name.trim().toLowerCase();
      if (!key) {
        return;
      }
      map.set(key, customFieldValues[index] ?? []);
    });
    return map;
  }, [customFields, customFieldValues]);

  const fileHeaderIndexMap = useMemo(() => {
    const map = new Map();
    fileHeaders.forEach((header, index) => {
      const key = header.trim().toLowerCase();
      if (!key) {
        return;
      }
      if (!map.has(key)) {
        map.set(key, index);
      }
    });
    return map;
  }, [fileHeaders]);

  // Sync table state from inputs if not edited locally
  useEffect(() => {
    if (isTableEdited) return;

    const base = recipientsMode === "file" ? fileHeaders : ["email"];
    const extra = customFields.map((field) => field.name.trim()).filter(Boolean);
    const seen = new Set();
    const combinedHeaders = [...base, ...extra].filter((name) => {
      const lower = name.toLowerCase();
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });

    const rows = [];
    if (recipientsMode === "file") {
      const customMaxLen = Math.max(
        ...Array.from(customFieldValueMap.values()).map((values) => values.length),
        0
      );
      const baseRowCount = fileRows.length > 0 ? fileRows.length : customMaxLen;
      const rowCount = baseRowCount;
      for (let i = 0; i < rowCount; i += 1) {
        const row = [];
        for (const header of combinedHeaders) {
          const key = header.trim().toLowerCase();
          const fileIndex = fileHeaderIndexMap.get(key);
          if (fileIndex !== undefined) {
            row.push(fileRows[i]?.[fileIndex] ?? "");
            continue;
          }
          const customValues = customFieldValueMap.get(key);
          row.push(customValues?.[i] ?? "");
        }
        rows.push(row);
      }
    } else {
      const emails = normalizedRecipients;
      const maxLength = Math.max(
        emails.length,
        ...Array.from(customFieldValueMap.values()).map((values) => values.length),
        0
      );
      const rowCount = maxLength;
      for (let i = 0; i < rowCount; i += 1) {
        const row = [];
        for (const header of combinedHeaders) {
          const key = header.trim().toLowerCase();
          if (key === "email") {
            row.push(emails[i] ?? "");
            continue;
          }
          const customValues = customFieldValueMap.get(key);
          row.push(customValues?.[i] ?? "");
        }
        rows.push(row);
      }
    }

    setTableHeaders(combinedHeaders);
    setTableRows(rows);
  }, [
    isTableEdited,
    recipientsMode,
    fileHeaders,
    fileRows,
    normalizedRecipients,
    customFields,
    fileHeaderIndexMap,
    customFieldValueMap,
    previewLimit
  ]);

  const handleCellChange = (rowIndex, colIndex, newValue) => {
    setIsTableEdited(true);
    setTableRows((prev) => {
      return prev.map((row, rIdx) => {
        if (rIdx === rowIndex) {
          const rowCopy = [...row];
          rowCopy[colIndex] = newValue;
          return rowCopy;
        }
        return row;
      });
    });
  };

  function handleSaveTable() {
    const emailColIndex = tableHeaders.findIndex((h) => h.toLowerCase() === "email");
    if (emailColIndex === -1) {
      window.alert("Cannot save: table must contain an 'email' column.");
      return;
    }

    const extraHeaders = tableHeaders.filter((_, idx) => idx !== emailColIndex);

    if (recipientsMode === "file") {
      setFileHeaders(tableHeaders);
      setFileRows(tableRows);
      setCustomFields([]);
    } else {
      const emails = tableRows.map((row) => row[emailColIndex] ?? "").filter(Boolean);
      setRecipientsTextValue(emails.join(", "));

      const newCustomFields = extraHeaders.map((header) => {
        const colIdx = tableHeaders.indexOf(header);
        const values = tableRows.map((row) => row[colIdx] ?? "").join(", ");
        const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        return { id, name: header, values };
      });
      setCustomFields(newCustomFields);
    }

    setIsTableEdited(false);
  }

  const handleAddRow = () => {
    setIsTableEdited(true);
    setTableRows((prev) => [...prev, Array(tableHeaders.length).fill("")]);
  };

  const handleDeleteRow = (rowIndex) => {
    setIsTableEdited(true);
    setTableRows((prev) => prev.filter((_, rIdx) => rIdx !== rowIndex));
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddColumn = () => {
    setColModal({
      isOpen: true,
      mode: "add",
      colIndex: null,
      value: "",
      error: ""
    });
  };

  const handleDeleteColumn = (colIndex) => {
    setDeleteConfirmModal({
      isOpen: true,
      colIndex,
      colName: tableHeaders[colIndex]
    });
  };

  const handleRenameColumn = (colIndex) => {
    setColModal({
      isOpen: true,
      mode: "rename",
      colIndex,
      value: tableHeaders[colIndex],
      error: ""
    });
  };

  const handleColModalSubmit = (e) => {
    e.preventDefault();
    const trimmed = colModal.value.trim();
    if (!trimmed) {
      setColModal((prev) => ({ ...prev, error: "Column name cannot be empty." }));
      return;
    }
    if (!/^[a-zA-Z0-9_ ]+$/.test(trimmed)) {
      setColModal((prev) => ({
        ...prev,
        error: "Column name must contain letters, numbers, spaces, or underscores only."
      }));
      return;
    }
    const lowerNew = trimmed.toLowerCase();

    if (colModal.mode === "add") {
      if (tableHeaders.some((h) => h.toLowerCase() === lowerNew)) {
        setColModal((prev) => ({ ...prev, error: "Column name already exists." }));
        return;
      }
      setIsTableEdited(true);
      setTableHeaders((prev) => [...prev, trimmed]);
      setTableRows((prev) => prev.map((row) => [...row, ""]));
    } else {
      const oldName = tableHeaders[colModal.colIndex];
      if (trimmed.toLowerCase() !== oldName.toLowerCase()) {
        if (
          tableHeaders.some(
            (h, cIdx) => cIdx !== colModal.colIndex && h.toLowerCase() === lowerNew
          )
        ) {
          setColModal((prev) => ({ ...prev, error: "Column name already exists." }));
          return;
        }
      }
      setIsTableEdited(true);
      setTableHeaders((prev) =>
        prev.map((h, cIdx) => (cIdx === colModal.colIndex ? trimmed : h))
      );
    }
    setColModal({ isOpen: false, mode: "add", colIndex: null, value: "", error: "" });
  };

  const handleDeleteColumnConfirm = () => {
    const { colIndex } = deleteConfirmModal;
    setIsTableEdited(true);
    setTableHeaders((prev) => prev.filter((_, cIdx) => cIdx !== colIndex));
    setTableRows((prev) =>
      prev.map((row) => row.filter((_, cIdx) => cIdx !== colIndex))
    );
    setDeleteConfirmModal({ isOpen: false, colIndex: null, colName: "" });
  };

  const previewGridTemplate = useMemo(() => {
    const columns = tableHeaders.map((header) => {
      const key = header.trim().toLowerCase();
      if (key === "email") {
        return "minmax(240px, 1.5fr)";
      }
      return "minmax(140px, 1fr)";
    });
    return `52px ${columns.join(" ")}`;
  }, [tableHeaders]);

  const handlePreviewNow = () => {
    if (htmlMode === "html") {
      setHtmlPreview(htmlInput);
    }
    if (htmlMode === "raw") {
      setRawPreview(rawInput);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    if (!formRef.current) {
      setError("Form is not ready. Please try again.");
      return;
    }

    const formData = new FormData(formRef.current);
    const subject = String(formData.get("subject") ?? "").trim();
    const htmlFile = formData.get("html");
    const htmlText = String(formData.get("htmlText") ?? "").trim();
    const rawText = String(formData.get("rawText") ?? "").trim();

    if (!subject) {
      setError("Email subject is required.");
      return;
    }

    if (Object.keys(customFieldErrors).length > 0) {
      setError("Please fix the custom fields before sending.");
      return;
    }

    const emailColIndex = tableHeaders.findIndex((h) => h.toLowerCase() === "email");
    if (emailColIndex === -1) {
      setError("The recipients list must contain an 'email' column.");
      return;
    }

    if (tableRows.length === 0) {
      setError("Please add at least one recipient.");
      return;
    }

    if (tableRows.length > maxEmails) {
      setError(`Please limit to ${maxEmails} recipients. Currently you have ${tableRows.length}.`);
      return;
    }

    if (htmlMode === "file") {
      if (!(htmlFile instanceof File) || htmlFile.size === 0) {
        setError("Please add an HTML file for the email body.");
        return;
      }
      if (htmlFile.size > maxFileSize) {
        setError("HTML file must be 2MB or less.");
        return;
      }
    } else if (htmlMode === "html" && !htmlText) {
      setError("Please paste the HTML email body.");
      return;
    } else if (htmlMode === "raw" && !rawText) {
      setError("Please paste the raw email body.");
      return;
    }

    // Convert table state to JSON array of objects
    const jsonRows = tableRows.map((row) => {
      const obj = {};
      tableHeaders.forEach((header, index) => {
        obj[header] = row[index] ?? "";
      });
      return obj;
    });

    formData.set("recipientsMode", "text");
    formData.set("recipientsText", JSON.stringify(jsonRows));
    formData.delete("recipients"); // delete file from FormData

    setIsSending(true);

    console.log("Here: " ,formData);

    try {
      const response = await fetch(`${API_BASE_URL}${ADMIN_EMAIL_ENDPOINT}`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: accessToken
          ? {
              authorization: `Bearer ${accessToken}`,
            }
          : undefined,
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError("error" in payload ? payload.error : "Send failed.");
      } else {
        setResult(payload);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Send failed.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <AdminSectionCard
        title="Email Sender"
        subtitle="Upload a CSV/Excel list with an email column or paste addresses to send a message."
      >
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Subject</h3>
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Required
              </span>
            </div>
            <div className="mt-4">
              <AdminField label="Email subject">
                <AdminInput
                  name="subject"
                  type="text"
                  placeholder="Add a subject"
                  maxLength={200}
                  required
                />
              </AdminField>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Recipients</h3>
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Input + Preview
              </span>
            </div>
            <div className="mt-4 flex flex-col gap-6">
              <div className="flex flex-col gap-4">
                <AdminField label="Recipients source">
                  <div className="relative">
                    <AdminSelect
                      name="recipientsMode"
                      value={recipientsMode}
                      onChange={(event) => {
                        setRecipientsMode(event.target.value);
                        setIsTableEdited(false);
                      }}
                      className={selectClass}
                    >
                      <option value="file">Upload recipients file</option>
                      <option value="text">Paste email list</option>
                    </AdminSelect>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      ▾
                    </span>
                  </div>
                </AdminField>

                {recipientsMode === "file" ? (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Recipients file (CSV, XLS, XLSX)
                    </label>
                    <input
                      id="recipientsFile"
                      name="recipients"
                      type="file"
                      accept=".csv,.xls,.xlsx"
                      onChange={handleRecipientsFileChange}
                      className="sr-only"
                    />
                    {!recipientsFileInfo ? (
                      <label
                        htmlFor="recipientsFile"
                        className="group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-6 text-center shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                      >
                        <FiUpload className="h-6 w-6 text-slate-400 group-hover:text-slate-600 transition" />
                        <span className="text-sm font-semibold text-slate-800">
                          Click to upload a CSV/XLS/XLSX file
                        </span>
                        <span className="text-xs text-slate-500">
                          Max size 2MB. Must include an "email" column.
                        </span>
                      </label>
                    ) : confirmRemoveRecipients ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm flex flex-col gap-3">
                        <p className="text-xs font-semibold text-red-800">
                          Are you sure you want to remove the uploaded recipients file? This will clear all parsed rows.
                        </p>
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveRecipients(false)}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleRemoveRecipientsFile();
                              setConfirmRemoveRecipients(false);
                            }}
                            className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-red-700"
                          >
                            Yes, Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                            <FiFileText className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800">
                              {recipientsFileInfo.split(" (")[0]}
                            </p>
                            <p className="text-xs text-slate-500">
                              {recipientsFileInfo.includes(" (") ? `Size: ${recipientsFileInfo.split(" (")[1].replace(")", "")}` : ""}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveRecipients(true)}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
                          title="Remove file"
                        >
                          <FiX className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {recipientsFileError ? (
                      <p className="text-xs text-red-600">
                        {recipientsFileError}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500">
                        Max size 2MB. Must contain {maxEmails} emails or fewer.
                      </p>
                    )}
                    {!hasEmailHeader && fileHeaders.length > 0 ? (
                      <p className="text-xs text-red-600">
                        The file must have an "email" column header.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Paste emails (comma or space separated)
                    </label>
                    <AdminTextarea
                      name="recipientsText"
                      rows={10}
                      placeholder="person1@example.com, person2@example.com"
                      className="min-h-[220px]"
                      maxLength={maxChars}
                      value={recipientsTextValue}
                      onChange={(event) => {
                        setRecipientsTextValue(event.target.value);
                        setIsTableEdited(false);
                      }}
                      required
                    />
                    <p className="text-xs text-slate-500">
                      Max {maxChars} characters, {maxEmails} emails.
                    </p>
                  </div>
                )}

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-900">Custom fields (Columns)</h4>
                    <button
                      type="button"
                      onClick={addCustomField}
                      className={buttonBaseClass}
                    >
                      Add field
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Field names use letters, numbers, and underscores only. Values must be comma
                    separated.
                  </p>
                  <div className="mt-4 flex flex-col gap-3">
                    {customFields.length === 0 ? (
                      <p className="text-xs text-slate-500">No custom fields added yet.</p>
                    ) : (
                      customFields.map((field) => (
                        <div
                          key={field.id}
                          className="rounded-2xl border border-slate-200 bg-slate-50 p-3"
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-600">Field</p>
                            <button
                              type="button"
                              onClick={() => removeCustomField(field.id)}
                              className="cursor-pointer rounded-full border border-red-200 px-2.5 py-0.5 text-xs font-semibold text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Field name
                              </label>
                              <input
                                type="text"
                                placeholder="Name"
                                value={field.name}
                                onChange={(event) =>
                                  updateCustomField(field.id, {
                                    name: event.target.value,
                                  })
                                }
                                className={inputBaseClass}
                              />
                            </div>
                            <div className="flex flex-col gap-2">
                              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Values (comma separated)
                              </label>
                              <textarea
                                rows={3}
                                placeholder="Alice, Bob, Carol"
                                value={field.values}
                                onChange={(event) =>
                                  updateCustomField(field.id, {
                                    values: event.target.value,
                                  })
                                }
                                className={adminCn(inputBaseClass, "min-h-[84px]")}
                              />
                            </div>
                          </div>
                          {customFieldErrors[field.id] ? (
                            <p className="mt-2 text-xs text-red-600">
                              {customFieldErrors[field.id]}
                            </p>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-300 bg-slate-100 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/85 pb-3">
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-slate-900">Recipients preview</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {isTableEdited ? "⚠️ Local modifications active" : "Synced with inputs"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isTableEdited && (
                      <button
                        type="button"
                        onClick={handleSaveTable}
                        className="cursor-pointer rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                      >
                        Save Changes
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleAddRow}
                      className="cursor-pointer rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      + Add Row
                    </button>
                    {/* <button
                      type="button"
                      onClick={handleAddColumn}
                      className="cursor-pointer rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      + Add Column
                    </button> */}
                  </div>
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Search in preview
                  </label>
                  <input
                    type="text"
                    placeholder="Search any column"
                    value={previewSearch}
                    onChange={(event) => setPreviewSearch(event.target.value)}
                    className={inputBaseClass}
                  />
                  <p className="text-xs text-slate-500">
                    Showing all {tableRows.length} rows.
                  </p>
                </div>
                <div className={adminCn("mt-3 overflow-auto rounded-2xl border border-slate-200 bg-white", tableRows.length > 15 && "max-h-[580px]")}>
                  {tableHeaders.length === 0 ? (
                    <div className="px-4 py-6 text-xs text-slate-500 bg-white">
                      Upload a file or paste emails to preview columns.
                    </div>
                  ) : (
                    <div className="min-w-max">
                      <div
                        className="grid border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600 items-center sticky top-0 z-10"
                        style={{ gridTemplateColumns: previewGridTemplate }}
                      >
                        <div className="px-3 py-2 text-right bg-slate-50">#</div>
                        {tableHeaders.map((header, colIndex) => (
                          <div
                            key={`${header}-${colIndex}`}
                            className="group relative px-3 py-2 flex items-center justify-between gap-1 bg-slate-50"
                          >
                            <span className="truncate font-semibold text-slate-700" title={header}>
                              {header}
                            </span>
                            <div className="hidden group-hover:flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleRenameColumn(colIndex)}
                                className="text-[9px] text-blue-600 hover:underline font-normal bg-white px-1 py-0.5 rounded border border-slate-200 shadow-sm transition"
                                title="Rename Column"
                              >
                                Rename
                              </button>
                              {header.toLowerCase() !== "email" && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteColumn(colIndex)}
                                  className="text-[9px] text-red-600 hover:underline font-normal bg-white px-1 py-0.5 rounded border border-slate-200 shadow-sm transition"
                                  title="Delete Column"
                                >
                                  Del
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {tableRows.length === 0 ? (
                        <div className="px-4 py-6 text-xs text-slate-500">
                          No preview rows available.
                        </div>
                      ) : (
                        tableRows
                          .filter((row) => {
                            if (!previewSearch.trim()) {
                              return true;
                            }
                            const query = previewSearch.toLowerCase();
                            return row.some((cell) =>
                              String(cell).toLowerCase().includes(query)
                            );
                          })
                          .map((row, index) => (
                            <div
                              key={`row-${index}`}
                              className={adminCn(
                                "grid text-xs text-slate-700 items-center border-b border-slate-100",
                                index % 2 === 0 ? "bg-white" : "bg-slate-50"
                              )}
                              style={{ gridTemplateColumns: previewGridTemplate }}
                            >
                              <div className="group/row flex h-full items-center justify-end gap-1 px-3 py-2 font-semibold text-slate-600">
                                <span className="group-hover/row:hidden">{index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRow(index)}
                                  className="hidden group-hover/row:flex text-red-500 hover:text-red-700 rounded hover:bg-red-50 p-0.5 transition"
                                  title="Delete Row"
                                >
                                  <FiX className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              {tableHeaders.map((_, colIndex) => (
                                <div key={colIndex} className="px-1 py-1">
                                  <input
                                    type="text"
                                    value={row[colIndex] || ""}
                                    onChange={(e) =>
                                      handleCellChange(index, colIndex, e.target.value)
                                    }
                                    className="w-full bg-transparent border border-transparent hover:border-slate-300 hover:bg-white focus:border-blue-500 focus:bg-white px-2 py-1 rounded outline-none transition text-xs text-slate-800"
                                  />
                                </div>
                              ))}
                            </div>
                          ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Message body</h3>
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Input + Preview
              </span>
            </div>
            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              <div className="flex flex-col gap-4">
                <AdminField label="Email body source">
                  <div className="relative">
                    <AdminSelect
                      name="htmlMode"
                      value={htmlMode}
                      onChange={(event) => setHtmlMode(event.target.value)}
                      className={selectClass}
                    >
                      <option value="file">Upload HTML file</option>
                      <option value="html">Paste HTML</option>
                      <option value="raw">Paste raw text</option>
                    </AdminSelect>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      ▾
                    </span>
                  </div>
                </AdminField>

                {htmlMode === "file" ? (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Email HTML file
                    </label>
                    <input
                      id="htmlFile"
                      name="html"
                      type="file"
                      accept=".html,.htm"
                      onChange={handleHtmlFileChange}
                      className="sr-only"
                    />
                    {!htmlFileInfo ? (
                      <label
                        htmlFor="htmlFile"
                        className="group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-6 text-center shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                      >
                        <FiUpload className="h-6 w-6 text-slate-400 group-hover:text-slate-600 transition" />
                        <span className="text-sm font-semibold text-slate-800">
                          Click to upload an HTML file
                        </span>
                        <span className="text-xs text-slate-500">Max size 2MB.</span>
                      </label>
                    ) : confirmRemoveHtml ? (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm flex flex-col gap-3">
                        <p className="text-xs font-semibold text-red-800">
                          Are you sure you want to remove the uploaded HTML file?
                        </p>
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveHtml(false)}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleRemoveHtmlFile();
                              setConfirmRemoveHtml(false);
                            }}
                            className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-red-700"
                          >
                            Yes, Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                            <FiFileText className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800">
                              {htmlFileInfo.split(" (")[0]}
                            </p>
                            <p className="text-xs text-slate-500">
                              {htmlFileInfo.includes(" (") ? `Size: ${htmlFileInfo.split(" (")[1].replace(")", "")}` : ""}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveHtml(true)}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
                          title="Remove file"
                        >
                          <FiX className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {htmlFileError ? (
                      <p className="text-xs text-red-600">{htmlFileError}</p>
                    ) : (
                      <p className="text-xs text-slate-500">Max size 2MB.</p>
                    )}
                  </div>
                ) : htmlMode === "html" ? (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Paste HTML body
                    </label>
                    <AdminTextarea
                      name="htmlText"
                      rows={12}
                      placeholder="<html>..."
                      className="min-h-[240px]"
                      maxLength={maxChars}
                      value={htmlInput}
                      onChange={(event) => setHtmlInput(event.target.value)}
                      required
                    />
                    <p className="text-xs text-slate-500">Max {maxChars} characters.</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Paste raw text body
                    </label>
                    <AdminTextarea
                      name="rawText"
                      rows={12}
                      placeholder="Plain text message"
                      className="min-h-[240px]"
                      maxLength={maxChars}
                      value={rawInput}
                      onChange={(event) => setRawInput(event.target.value)}
                      required
                    />
                    <p className="text-xs text-slate-500">Max {maxChars} characters.</p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handlePreviewNow}
                    className={adminCn(buttonBaseClass, "px-4 py-2 text-xs")}
                  >
                    Update preview
                  </button>
                  <span className="text-xs text-slate-500">
                    Preview updates 2 seconds after typing stops.
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-900">Message preview</h4>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Preview only
                  </span>
                </div>
                {htmlMode === "raw" ? (
                  <pre className="mt-3 max-h-[360px] overflow-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                    {rawPreview || "No text yet."}
                  </pre>
                ) : htmlMode === "file" ? (
                  <p className="mt-3 text-xs text-slate-600">
                    Upload an HTML file to send. Preview is shown for pasted HTML.
                  </p>
                ) : (
                  <div className="mt-3 h-[360px] overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <iframe
                      title="HTML preview"
                      className="h-full w-full"
                      sandbox=""
                      srcDoc={htmlPreviewDoc}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {result ? (
            <div className="flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <p>
                Sent {result.sent} emails. Failed {result.failed}.
              </p>
              {result.failures?.length ? (
                <div className="max-h-40 overflow-auto text-xs">
                  {result.failures.map((failure) => (
                    <p key={failure.email}>
                      {failure.email}: {failure.error}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center justify-end">
            <AdminButton type="submit" disabled={isSending} className="px-6">
              {isSending ? "Sending..." : "Send Emails"}
            </AdminButton>
          </div>
        </form>
      </AdminSectionCard>

      {/* Column Add/Rename Modal */}
      {colModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl transition-all">
            <h3 className="text-lg font-bold text-slate-900">
              {colModal.mode === "add" ? "Add New Column" : "Rename Column"}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {colModal.mode === "add"
                ? "Create a new column placeholder for interpolation."
                : `Rename the placeholder column.`}
            </p>
            <form onSubmit={handleColModalSubmit} className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Column Name
                </label>
                <input
                  type="text"
                  value={colModal.value}
                  onChange={(e) =>
                    setColModal((prev) => ({ ...prev, value: e.target.value, error: "" }))
                  }
                  placeholder="e.g. Phone Number"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  autoFocus
                />
                {colModal.error && (
                  <p className="mt-1.5 text-xs font-semibold text-red-600">{colModal.error}</p>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() =>
                    setColModal({ isOpen: false, mode: "add", colIndex: null, value: "", error: "" })
                  }
                  className="cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cursor-pointer rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Column Confirmation Modal */}
      {deleteConfirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl transition-all">
            <h3 className="text-lg font-bold text-slate-900">Delete Column</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete the column{" "}
              <strong className="text-slate-900">"{deleteConfirmModal.colName}"</strong>? This will
              permanently delete all row values under this column.
            </p>
            <div className="mt-6 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirmModal({ isOpen: false, colIndex: null, colName: "" })}
                className="cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteColumnConfirm}
                className="cursor-pointer rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailSender;
